import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import AppShell from './components/layout/AppShell';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import ClientsPage from './pages/ClientsPage';
import LeadsPage from './pages/LeadsPage';
import ProjectsPage from './pages/ProjectsPage';
import RenewalsPage from './pages/RenewalsPage';
import JobOrdersPage from './pages/JobOrdersPage';
import TakhierPage from './pages/TakhierPage';
import ServicesPage from './pages/ServicesPage';
import InvoicesPage from './pages/InvoicesPage';
import ServersPage from './pages/ServersPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import PaymentGatewaysPage from './pages/PaymentGatewaysPage';
import ExpensesPage from './pages/ExpensesPage';
import PayrollPage from './pages/PayrollPage';
import ContractsPage from './pages/ContractsPage';
import RolesPage from './pages/RolesPage';
import CompanyDocsPage from './pages/CompanyDocsPage';
import ProjectClosuresPage from './pages/ProjectClosuresPage';
import PartnershipsPage from './pages/PartnershipsPage';
import ModulePasswordsPage from './pages/ModulePasswordsPage';
import UsersPage from './pages/UsersPage';
import './index.css';

const queryClient = new QueryClient({ defaultOptions:{ queries:{ staleTime:30000, retry:1 } } });

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-center"><div className="spinner"/></div>;
  return user ? children : <Navigate to="/login" replace/>;
}

function AppRoutes() {
  const { user } = useAuth();
  if (user === null) return (
    <Routes>
      <Route path="/login" element={<LoginPage/>}/>
      <Route path="*" element={<Navigate to="/login" replace/>}/>
    </Routes>
  );
  return (
    <PrivateRoute>
      <AppShell>
        <Routes>
          <Route path="/" element={<Dashboard/>}/>
          <Route path="/clients" element={<ClientsPage/>}/>
          <Route path="/leads" element={<LeadsPage/>}/>
          <Route path="/projects" element={<ProjectsPage/>}/>
          <Route path="/job-orders" element={<JobOrdersPage/>}/>
          <Route path="/renewals" element={<RenewalsPage/>}/>
          <Route path="/takhier" element={<TakhierPage/>}/>
          <Route path="/services" element={<ServicesPage/>}/>
          <Route path="/invoices" element={<InvoicesPage/>}/>
          <Route path="/servers" element={<ServersPage/>}/>
          <Route path="/expenses" element={<ExpensesPage/>}/>
          <Route path="/subscriptions" element={<SubscriptionsPage/>}/>
          <Route path="/payment-gateways" element={<PaymentGatewaysPage/>}/>
          <Route path="/payroll" element={<PayrollPage/>}/>
          <Route path="/contracts" element={<ContractsPage/>}/>
          <Route path="/roles" element={<RolesPage/>}/>
          <Route path="/company-docs" element={<CompanyDocsPage/>}/>
          <Route path="/project-closures" element={<ProjectClosuresPage/>}/>
          <Route path="/users" element={<UsersPage/>}/>
          <Route path="/partnerships" element={<PartnershipsPage/>}/>
          <Route path="/module-passwords" element={<ModulePasswordsPage/>}/>
          <Route path="*" element={<Navigate to="/" replace/>}/>
        </Routes>
      </AppShell>
    </PrivateRoute>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" toastOptions={{
            style:{background:'#182219',color:'#e8f0ec',border:'1px solid #243028',fontSize:'13px'},
            success:{iconTheme:{primary:'#00c97a',secondary:'#0a0f0d'}},
          }}/>
          <AppRoutes/>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
